import { fg } from '@atlaskit/platform-feature-flags';

import { type AgentMetadata, type Conversation } from './types';

/**
 * Creates minimal conversation-agent metadata when we know only the agent id.
 * This lets optimistic/local conversation state carry the conversation-associated
 * `conversation.agent.agent_id` before full backend metadata has hydrated. The
 * blank display fields are intentional placeholders; UI should resolve/display
 * full agent metadata separately instead of treating this as a complete agent.
 *
 * Returns `undefined` for missing/empty ids so we never seed a blank agent.
 */
export const createProvisionalConversationAgent = (agentId?: string): AgentMetadata | undefined => {
	if (!agentId) {
		return undefined;
	}

	return {
		agent_id: agentId,
		agent_named_id: agentId,
		agent_name: '',
		creator_type: '',
	};
};

/**
 * Resolve the conversation agent id used as the no-override routing fallback.
 *
 * Under `rovo_chat_agent_selection` the stored `conversation.agent.agent_id` is the
 * conversation agent when present; otherwise (or gate-off) we fall back to the caller
 * intent supplied in `fallbackAgentId`.
 *
 * rovo_chat_agent_selection cleanup: remove the gate and the `fallbackAgentId`
 * caller-id fallback once stored `conversation.agent.agent_id` is always populated.
 */
export const resolveConversationAgentIdForRouting = ({
	conversation,
	fallbackAgentId,
}: {
	conversation: Conversation | undefined;
	fallbackAgentId: string;
}): string => {
	const storedConversationAgentId = conversation?.agent?.agent_id || undefined;
	return storedConversationAgentId && fg('rovo_chat_agent_selection')
		? storedConversationAgentId
		: fallbackAgentId;
};
